import React from "react";
import { Box, Typography, Tooltip } from "@mui/material";
import { Add, Visibility, VisibilityOff, PersonAdd } from "@mui/icons-material";
import MicIcon from "@mui/icons-material/Mic";
import MusicNoteIcon from "@mui/icons-material/MusicNote";
import dayjs from "dayjs";
import { WEEKDAYS, DATE_HEADER_HEIGHT, ROW_HEIGHT, ROW_GAP, getQuarterStart, hexToRgba } from "./calendarUtils";

const CalendarGrid = ({ gridRef, calendarDays, getMonthStartsInWeek, getRowHeight, showRegistrations, registrationsByDate, setSelectedDemographicsDate, setDemographicsModalOpen, handleOpenAddModal, currentQuarter, visibleEvents, getEventBarSegments, eventSlots, getEventStatus, cellWidth, dragState, handleBarClick, handleMoveStart, handleResizeStart }) => (
<>
          {/* Calendar Grid - Scrollable */}
          <Box sx={{ maxHeight: "calc(100vh - 320px)", overflowY: "auto" }}>
            <div className="gantt-calendar">
              <div className="gantt-weekdays gantt-weekdays-sticky">
                {WEEKDAYS.map((day) => (
                  <div key={day} className="gantt-weekday">{day}</div>
                ))}
              </div>

              <div className="gantt-grid" ref={gridRef}>
                {Array.from({ length: Math.ceil(calendarDays.length / 7) }).map(
                  (_, weekIndex) => {
                    const monthStarts = getMonthStartsInWeek(weekIndex);
                    
                    return (
                      <div
                        key={weekIndex}
                        className="gantt-week-row"
                        style={{ minHeight: getRowHeight(weekIndex) }}
                      >
                        {calendarDays.slice(weekIndex * 7, (weekIndex + 1) * 7).map(
                          (day, dayIndex) => {
                            const isToday = day.date.isSame(dayjs(), "day");
                            const isPast = day.date.isBefore(dayjs(), "day");
                            const hasMonthLabel = day.isMonthStart || day.isFirstQuarterDay || day.isFirstMonthOfQuarter;
                            // Show month name on 1st of any month, or on first occurrence of first month of quarter
                            const quarterStartDate = getQuarterStart(currentQuarter.year, currentQuarter.quarter);
                            const showMonthName = day.isMonthStart || day.isFirstMonthOfQuarter || (day.isFirstQuarterDay && day.date.month() === quarterStartDate.month());
                            return (
                              <div
                                key={dayIndex}
                                className={`gantt-day-cell ${!day.isCurrentMonth ? "other-month" : ""} ${isToday ? "today" : ""} ${isPast ? "past" : ""} ${hasMonthLabel ? "month-start" : ""}`}
                              >
                                <div className="gantt-date-wrapper">
                                  <span className="gantt-date-number">{day.date.date()}</span>
                                  {showMonthName && (
                                    <span className="gantt-month-name">{day.monthName}</span>
                                  )}
                                  {showRegistrations && registrationsByDate[day.date.format("YYYY-MM-DD")]?.length > 0 && (
                                    <Tooltip title={`${registrationsByDate[day.date.format("YYYY-MM-DD")].length} new registrations - Click for details`} arrow>
                                      <span 
                                        className="gantt-registration-badge"
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          setSelectedDemographicsDate(day.date.format("YYYY-MM-DD"));
                                          setDemographicsModalOpen(true);
                                        }}
                                        style={{ cursor: "pointer" }}
                                      >
                                        <PersonAdd sx={{ fontSize: 10 }} />
                                        {registrationsByDate[day.date.format("YYYY-MM-DD")].length}
                                      </span>
                                    </Tooltip>
                                  )}
                                </div>
                                <div 
                                  className="gantt-cell-add-btn"
                                  onClick={() => handleOpenAddModal(day.date)}
                                  title={`Add challenge on ${day.date.format("MMM D, YYYY")}`}
                                >
                                  <Add sx={{ fontSize: 20 }} />
                                </div>
                              </div>
                            );
                          }
                        )}

                        <div className="gantt-bars-overlay">
                          {visibleEvents.map((event) => {
                            const segments = getEventBarSegments(event);
                            const slot = eventSlots.get(event.id) || 0;
                            const statusInfo = getEventStatus(event);

                            return segments
                              .filter((seg) => seg.weekRow === weekIndex)
                              .map((seg, segIndex) => {
                                const left = seg.startCol * cellWidth;
                                const width = (seg.endCol - seg.startCol + 1) * cellWidth;
                                const top = DATE_HEADER_HEIGHT + slot * (ROW_HEIGHT + ROW_GAP);

                                return (
                                  <Tooltip
                                    key={`${event.id}-${segIndex}`}
                                    title={
                                      <Box>
                                        <Typography variant="body2" fontWeight={600}>
                                          {event.title}
                                        </Typography>
                                        {event.type === "challenge" && event.category && (
                                          <Typography variant="caption" display="block">{event.category}</Typography>
                                        )}
                                        {event.type === "contest" && event.prize && (
                                          <Typography variant="caption" display="block">Prize: {event.prize}</Typography>
                                        )}
                                        {event.type === "karaoke" && event.artist && (
                                          <Typography variant="caption" display="block">Artist: {event.artist}</Typography>
                                        )}
                                        {event.type === "karaoke" && event.durationFormatted && (
                                          <Typography variant="caption" display="block">
                                            Song Length: {event.durationFormatted}
                                          </Typography>
                                        )}
                                        {event.type === "dance" && event.durationFormatted && (
                                          <Typography variant="caption" display="block">
                                            Track Length: {event.durationFormatted}
                                          </Typography>
                                        )}
                                        {event.type === "contest" && (
                                          <Typography 
                                            variant="caption" 
                                            display="block" 
                                            sx={{ 
                                              mt: 0.5, 
                                              color: event.isCurrentlyShowing ? "#4ade80" : "#fbbf24",
                                              fontWeight: 600 
                                            }}
                                          >
                                            {event.isCurrentlyShowing ? "✓ Visible in app" : "○ Not yet visible"}
                                          </Typography>
                                        )}
                                        <Typography variant="caption" display="block">
                                          {dayjs(event.startDate).format("MMM D")} - {dayjs(event.endDate).format("MMM D, YYYY")}
                                        </Typography>
                                        {event.submissionCount != null && (
                                          <Typography variant="caption" display="block" sx={{ mt: 0.5, fontWeight: 600 }}>
                                            {event.type === "challenge" ? "Posts" : (event.type === "karaoke" || event.type === "dance") ? "Submissions" : "Entries"}: {event.submissionCount}
                                          </Typography>
                                        )}
                                        {event.type !== "karaoke" && event.type !== "dance" && (
                                          <Typography variant="caption" display="block" sx={{ mt: 0.5, color: "#90caf9" }}>
                                            Click to edit
                                          </Typography>
                                        )}
                                      </Box>
                                    }
                                    arrow
                                    placement="top"
                                  >
                                    <div
                                      className={`gantt-bar ${seg.isStart ? "bar-start" : ""} ${seg.isEnd ? "bar-end" : ""} ${event.type === "contest" ? "bar-contest" : ""} ${event.type === "karaoke" ? "bar-karaoke" : ""} ${event.type === "dance" ? "bar-dance" : ""} ${dragState?.event?.id === event.id ? "dragging" : ""}`}
                                      style={{
                                        left: `${left + (seg.isStart ? 4 : 0)}px`,
                                        width: `${width - (seg.isStart ? 4 : 0) - (seg.isEnd ? 4 : 0)}px`,
                                        top: `${top}px`,
                                        backgroundColor: hexToRgba(statusInfo.color, 0.55),
                                        borderColor: statusInfo.borderColor,
                                        cursor: (event.type === 'karaoke' || event.type === 'dance') ? 'default' : 'grab',
                                      }}
                                      onClick={(e) => !dragState && event.type !== 'karaoke' && event.type !== 'dance' && handleBarClick(event, e)}
                                      onMouseDown={(e) => event.type !== 'karaoke' && event.type !== 'dance' && handleMoveStart(e, event)}
                                    >
                                      {/* Resize handle at start - only for challenges */}
                                      {seg.isStart && event.type === 'challenge' && (
                                        <div 
                                          className="gantt-bar-resize-handle gantt-bar-resize-start"
                                          onMouseDown={(e) => handleResizeStart(e, event, 'start')}
                                        />
                                      )}
                                      {/* Continuation circle at start (coming from previous week) */}
                                      {!seg.isStart && (
                                        <span 
                                          className="gantt-bar-continuation gantt-bar-continuation-start"
                                          style={{ borderColor: statusInfo.borderColor }}
                                        />
                                      )}
                                      {seg.isStart && (
                                        <>
                                          {/* Eye icon for contest visibility status */}
                                          {event.type === 'contest' && (
                                            <Tooltip 
                                              title={event.isCurrentlyShowing ? "Visible in app" : "Not yet visible in app"} 
                                              arrow 
                                              placement="top"
                                            >
                                              {event.isCurrentlyShowing ? (
                                                <Visibility sx={{ fontSize: 16, color: '#16a34a', flexShrink: 0 }} />
                                              ) : (
                                                <VisibilityOff sx={{ fontSize: 16, color: '#9ca3af', flexShrink: 0 }} />
                                              )}
                                            </Tooltip>
                                          )}
                                          {/* Mic icon for karaoke */}
                                          {event.type === 'karaoke' && (
                                            <MicIcon sx={{ fontSize: 16, color: '#7c3aed', flexShrink: 0 }} />
                                          )}
                                          {/* Music note icon for dance */}
                                          {event.type === 'dance' && (
                                            <MusicNoteIcon sx={{ fontSize: 16, color: '#14b8a6', flexShrink: 0 }} />
                                          )}
                                          {event.logo && (
                                            <img src={event.logo} alt="" className="gantt-bar-logo" />
                                          )}
                                          {/* Show duration for karaoke/dance, count for others */}
                                          {event.type === 'karaoke' ? (
                                            <span className="gantt-bar-count" style={{ color: '#7c3aed' }}>
                                              {event.durationFormatted}{event.submissionCount != null && ` (${event.submissionCount})`}
                                            </span>
                                          ) : event.type === 'dance' ? (
                                            <span className="gantt-bar-count" style={{ color: '#14b8a6' }}>
                                              {event.durationFormatted}{event.submissionCount != null && ` (${event.submissionCount})`}
                                            </span>
                                          ) : event.submissionCount != null ? (
                                            <span className="gantt-bar-count">({event.submissionCount})</span>
                                          ) : null}
                                          <span className="gantt-bar-title">{event.title}</span>
                                        </>
                                      )}
                                      {/* Continuation circle at end (going to next week) */}
                                      {!seg.isEnd && (
                                        <span 
                                          className="gantt-bar-continuation gantt-bar-continuation-end"
                                          style={{ borderColor: statusInfo.borderColor }}
                                        />
                                      )}
                                      {/* Resize handle at end - only for challenges */}
                                      {seg.isEnd && event.type === 'challenge' && (
                                        <div 
                                          className="gantt-bar-resize-handle gantt-bar-resize-end"
                                          onMouseDown={(e) => handleResizeStart(e, event, 'end')}
                                        />
                                      )}
                                    </div>
                                  </Tooltip>
                                );
                              });
                          })}
                        </div>
                      </div>
                    );
                  }
                )}
              </div>
            </div>
          </Box>
</>
);

export default CalendarGrid;
